<template>
  <component
    :is="component"
    v-if="offer"
    :to="isPlaceOrder || noPrice ? undefined : offer.click_url"
    :offer="isPlaceOrder ? undefined : offer"
    :block-type="isPlaceOrder || noPrice ? undefined : 'monitoring'"
    :target="!isPlaceOrder ? '_blank' : undefined"
    class="monitoring-offer"
    :class="{ 'monitoring-offer__no_price': noPrice }"
    @click="setOrder"
  >
    <div class="monitoring-block">
      <div class="monitoring-offer-info">
        <div class="monitoring-price heading-s">
          <div class="heading-s">{{ price }} ₽</div>
        </div>
        <div v-if="shop" class="monitoring-shop">
          <img
            v-if="icon"
            class="monitoring-shop__icon"
            :src="shopIcon"
            :alt="shop.name"
            @error="setIcon"
          />
          <UiIcon v-else class="monitoring-shop__icon" name="shop" />
          <div class="monitoring-shop__name body-m-regular">
            {{ shop.name }}
          </div>
          <div
            v-if="shop.proved_merchant && !noPrice"
            class="monitoring-shop__proved"
          />
        </div>
      </div>
      <div
        v-if="showMinPrice && !noPrice"
        class="monitoring-best-min body-m-medium"
      >
        Самая низкая цена на Price
      </div>
    </div>
    <div class="monitoring-arrow">
      <UiIcon size="20" name="right" stroke="#363EF8" />
    </div>
  </component>
</template>

<script setup lang="ts">
import type { ModelOfferItem } from '~/interfaces/api';
import CpcLink from '~/components/ui/CpcLink.vue';
import { useModalsStore } from '~/stores/modals';

const emits = defineEmits(['close']);
const props = defineProps({
  offer: {
    type: Object as () => ModelOfferItem,
    required: true,
  },
  type: {
    type: String as () => 'min_price' | 'avg_price' | undefined,
    default: undefined,
  },
  isBlock: {
    type: Boolean,
    default: true,
  },
  isStickerModal: {
    type: Boolean,
    default: false,
  },
});
const modalsStore = useModalsStore();
const icon = ref(true);
const shop = computed(() => {
  return props.offer.shop_info || null;
});
const shopIcon = computed(() => {
  if (props.offer?.shop_info && props.offer.shop_info?.icon) {
    return (props.offer.shop_info?.icon).replace(/^http:/, 'https:');
  }
  if (props.offer?.shop_info && props.offer.shop_info.site) {
    return `${props.offer.shop_info.site}/favicon.ico`.replace(
      /^http:/,
      'https:'
    );
  }
  return '';
});
const showMinPrice = computed(() => {
  if (props.isStickerModal) return false;
  return props.type === 'min_price' || !!props.offer?.has_min_price;
});
const isPlaceOrder = computed(() => props.offer?.cpo);
const noPrice = computed(
  () => !props.offer?.is_priceru_offer && !props.isStickerModal
);
const price = computed(() => {
  const inputPrice = props.offer.price || 0;
  if (!inputPrice) return '0';
  return (
    Math.ceil(
      Math.sign(inputPrice) === -1
        ? Math.ceil(inputPrice * -1)
        : Math.ceil(inputPrice) * 100
    ) / 100
  ).toLocaleString('ru');
});
const component = computed(() => {
  if (noPrice.value) return 'div';
  return isPlaceOrder.value ? 'div' : CpcLink;
});
const setOrder = () => {
  if (!isPlaceOrder.value) return;
  modalsStore.setData({
    name: 'order',
    data: {
      offer: props.offer,
      position: 1,
      blockType: 'monitoring',
    },
  });
  emits('close');
};
const setIcon = () => {
  icon.value = false;
};
</script>

<style scoped lang="scss">
.monitoring-offer {
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  padding: 12px 8px 12px 12px;
  border-radius: 8px;
  background: $light-blue;
  @include sm-block {
    justify-content: flex-start;
  }

  .monitoring-block {
    width: calc(100% - 24px);
  }

  .monitoring-arrow {
    width: 20px;
  }

  .monitoring-offer-info {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }

  .monitoring-price {
    width: calc(50% - 2px);
    @include sm-block {
      width: 160px;
      margin-right: 4px;
    }
    @include md-block {
      width: calc(50% - 2px);
    }

    div {
      position: relative;
      display: flex;
      justify-content: center;
      width: max-content;
      padding: 4px 6px 0;
      border-radius: 8px;
      background: $main-cta;
      height: 28px;
      color: $white;

      &:before {
        content: '';
        position: absolute;
        width: 24px;
        height: 24px;
        right: -10px;
        top: -6px;
        background: url('/static/images/icon/price-border.svg') center / contain
          no-repeat;
      }
    }
  }

  .monitoring-shop {
    display: flex;
    align-items: center;
    width: calc(50% - 2px);
    @include sm-block {
      width: calc(100% - 164px);
    }
    @include md-block {
      width: calc(50% - 2px);
    }

    .monitoring-shop__icon {
      width: 16px;
      height: 16px;
      margin-right: 4px;
    }

    .monitoring-shop__name {
      padding-top: 2px;
      color: $achromatic-black;
      margin-right: 4px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      overflow-wrap: anywhere;
      max-width: 100%;
      width: fit-content;
    }

    .monitoring-shop__proved {
      width: 14px;
      height: 14px;
      background: url('/static/images/icon/proved.svg') center / contain
        no-repeat;
    }
  }

  .monitoring-best-min {
    position: relative;
    display: flex;
    margin-top: 4px;
    padding-top: 2px;
    height: 16px;
    align-items: center;
    padding-left: 20px;
    color: $pink2;
    @include lg-block {
      margin-top: 8px;
    }

    &:before {
      content: '';
      position: absolute;
      width: 12px;
      height: 12px;
      left: 0;
      top: calc(50% - 6px);
      background: $pink2;
      mask: url('/static/images/icon/percent.svg') center / contain no-repeat;
    }
  }

  &__no_price {
    border-radius: 0;
    background: none;
    pointer-events: none;

    .monitoring-arrow {
      display: none;
    }

    .monitoring-shop__name {
      opacity: 0.7;
    }

    .monitoring-price {
      width: calc(50% - 2px);
      @include sm-block {
        width: 160px;
        margin-right: 4px;
      }
      @include md-block {
        width: calc(50% - 2px);
      }

      div {
        color: $achromatic-black;
        background: none;
        opacity: 0.7;

        &:before {
          display: none;
        }
      }
    }
  }
}
</style>
